<template>
    <application-layout>
      <v-layout
        v-if="loadingStatus"
        justify-center
        key="load-state"
      >
        <v-progress-circular
          :size="60"
          :width="6"
          indeterminate
          class="my-4"
        />
      </v-layout>
      <v-form v-else>
          <v-container>
              <h1>
                  <span v-if="user.first_name && user.last_name">
                      Editing {{ fullName }}'s Profile
                  </span>
                  <span v-else>Edit Profile</span>
              </h1>
              <v-form class="mt-4">
                  <v-layout wrap class="mt-2">
                      <v-flex xs12 sm4>
                          <app-input
                              data-testid="edit-username-input"
                              class="edit-username-input"
                              :value="user.username"
                              label="Username"
                              filled
                              disabled
                          >
                            Username
                          </app-input>
                      </v-flex>
                      <v-flex xs12 sm4 class="px-4">
                          <app-input
                              data-testid="edit-user-email-input"
                              class="edit-user-email-input"
                              :value="user.email"
                              label="Email"
                              :rules="{ email: true }"
                              filled
                              disabled
                          >
                            Email
                          </app-input>
                      </v-flex>
                      <v-flex xs12 sm4>
                          <app-input
                              data-testid="edit-user-first-name-input"
                              class="edit-user-first-name-input"
                              v-model.trim="user.first_name"
                              label="First Name"
                              :rules="{ required: true}"
                              filled
                          >
                            First Name
                          </app-input>
                      </v-flex>
                      <v-flex xs12 sm4>
                          <app-input
                              data-testid="edit-user-last-name-input"
                              class="edit-user-last-name-input"
                              v-model.trim="user.last_name"
                              label="Last Name"
                              :rules="{ required: true }"
                              filled
                          >
                            Last Name
                          </app-input>
                      </v-flex>
                      <v-flex xs12 sm4 class="px-4">
                        <app-input
                          data-testid="edit-user-ctm-id-input"
                          class="edit-user-ctm-id-input"
                          v-model.trim="user.ctm_id"
                          label="CTM ID"
                          :rules="{ required: true }"
                          filled
                        >
                          CTM ID
                        </app-input>
                      </v-flex>
                      <v-flex xs12 sm4>
                        <app-input
                          data-testid="edit-user-avatar-input"
                          class="edit-user-avatar-input"
                          v-model.trim="user.avatar_url"
                          label="Avatar URL"
                          :rules="{ required: true, max: 255 }"
                          filled
                        >
                          Avatar URL
                        </app-input>
                      </v-flex>
                      <v-flex xs12 class="mt-4 mb-2">
                        <v-select
                          data-testid="edit-user-roles-menu"
                          class="edit-user-roles-menu"
                          v-model="user.assigned_roles"
                          label="User Roles"
                          :items="roles"
                          :rules="inputRules"
                          item-text="name"
                          item-value="id"
                          hint="Roles determine what a user is able to do in the call center platform"
                          persistent-hint
                          chips
                          multiple
                          outlined
                        />
                      </v-flex>
                      <v-flex xs12 class="my-2">
                          <v-btn
                              data-testid="reset-user-password-btn"
                              class="reset-user-password-btn mt-2"
                              color="orange"
                              @click="handlePasswordReset">
                              Reset User's Password
                          </v-btn>
                      </v-flex>
                  </v-layout>
                  <v-divider class="my-4" />
                  <v-layout class="py-2">
                      <v-btn
                          data-testid="edit-user-cancel-btn"
                          class="edit-user-cancel-btn ml-0 error"
                          @click="() => { this.$router.back(); }"
                      >
                          Cancel
                      </v-btn>
                      <v-spacer />
                      <v-btn
                          data-testid="edit-user-save-btn"
                          class="edit-user-save-btn"
                          color="primary"
                          @click="updateUser"
                          :loading="loading"
                          :disabled="!formValid"
                      >
                          Save
                      </v-btn>
                  </v-layout>
              </v-form>
          </v-container>
      </v-form>
    </application-layout>
</template>

<script>
import { avatarRules, inputRules } from "@/helpers/form-rules";
import ApplicationLayout from "@/layouts/ApplicationLayout";

export default {
  data() {
    return {
      loading: false,
      userLoading: false,
      roleLoading: false,
      roles: [],
      avatarRules: avatarRules,
      inputRules: inputRules,
      user: ""
    };
  },

  computed: {
    loadingStatus() {
      return this.userLoading && this.roleLoading;
    },
    formValid() {
      return !!(this.user.first_name &&
          this.user.last_name &&
          this.user.ctm_id &&
          this.user.avatar_url &&
          this.user.assigned_roles.length
      );
    },
    fullName() {
      return `${this.user.first_name} ${this.user.last_name}`;
    }
  },

  components: {
    ApplicationLayout
  },

  created() {
    this.userLoading = true;
    this.roleLoading = true;
    this.$store.dispatch("getUserProfile", this.$route.params.userId)
      .then(response => {
        this.user = response.data.user;
        this.userLoading = false;
      });

    this.$store.dispatch("getCallCenterRoles")
      .then(response => {
        this.roles = response.data.roles;
        this.roleLoading = false;
      });
  },

  methods: {
    updateUser() {
      this.loading = true;

      this.$store.dispatch("updateUserProfile", this.user)
        .then(() => {
          this.$store.dispatch("handleSuccess", "User updated successfully!");
          this.loading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.loading = false;
        });
    },

    handlePasswordReset() {
      this.$store.dispatch("forgotPassword", this.user.id);
    }
  }
};
</script>
